import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isAtOrOverLimit } from '@covered-calls/shared';
import type { PlanUsage } from '@covered-calls/shared';
import { TradeList } from '../components/TradeList';
import { TradeForm } from '../components/TradeForm';
import { getProfile } from '../services/profile-service';
import { planDisplayName } from './plan-catalog';
import { PlanTierIcon } from '../components/PlanTierIcon';

// Query marker the Stripe success_url appends when the browser returns from a
// completed Checkout (Req 11.2). The infra-side success_url is configured to
// land back on this page carrying `?checkout=success`; the frontend only needs
// to detect its own return marker. Entitlement state is NEVER taken from this
// param — the webhook is the source of truth (Req 11.1) — so on detecting it we
// simply re-fetch the profile and render the reconciled plan block.
const CHECKOUT_SUCCESS_PARAM = 'checkout';
const CHECKOUT_SUCCESS_VALUE = 'success';

export function Trades() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(searchParams.get('new') === 'true');

  // Plan usage block from the profile GET (Decision 8), used to render the
  // Usage_Indicator proactively in the TradeForm. Non-blocking: if the fetch
  // fails or carries no plan block, the indicator simply isn't shown.
  const [planUsage, setPlanUsage] = useState<PlanUsage | undefined>(undefined);

  // True only while showing the post-checkout success confirmation, driven by
  // the return marker. The plan shown is always the reconciled `profile.plan`.
  const returnedFromCheckout = searchParams.get(CHECKOUT_SUCCESS_PARAM) === CHECKOUT_SUCCESS_VALUE;
  const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(returnedFromCheckout);

  useEffect(() => {
    let active = true;
    getProfile()
      .then((profile) => {
        if (active && profile.plan) {
          setPlanUsage(profile.plan);
        }
      })
      .catch(() => {
        // Non-blocking — usage indicator just won't be shown.
      });
    return () => {
      active = false;
    };
    // Re-run when we return from Checkout so entitlement state is reconciled
    // from the webhook-updated profile, never from the redirect params.
  }, [returnedFromCheckout]);

  const atLimit = planUsage ? isAtOrOverLimit(planUsage.effectiveLimit, planUsage.positionsCreated) : false;
  const limitReason = atLimit && planUsage
    ? `You've reached your trial limit of ${planUsage.effectiveLimit} positions.`
    : undefined;

  const handleViewPlans = () => {
    navigate('/plans');
  };

  const handleNewTrade = () => {
    if (atLimit) return;
    setShowForm(true);
    setSearchParams({ new: 'true' });
  };

  const handleFormClose = () => {
    setShowForm(false);
    setSearchParams({});
  };

  return (
    <div>
      {showForm ? (
        <div>
          <button
            onClick={handleFormClose}
            style={{
              marginBottom: '1rem',
              background: 'none',
              border: 'none',
              color: 'var(--color-brand)',
              cursor: 'pointer',
              padding: '0.25rem 0',
              fontSize: '1rem',
              minHeight: 'auto',
              textDecoration: 'underline',
            }}
          >
            &larr; Back to positions
          </button>
          <TradeForm onSuccess={() => handleFormClose()} planUsage={planUsage} />
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h1 style={{ margin: 0 }}>Trades</h1>
            <button
              onClick={handleNewTrade}
              disabled={atLimit}
              aria-disabled={atLimit}
              title={limitReason}
              style={{
                padding: '0.5rem 1.25rem',
                backgroundColor: 'var(--color-brand)',
                color: 'var(--color-on-brand)',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 500,
                minHeight: '44px',
                ...(atLimit ? { cursor: 'not-allowed', opacity: 0.6 } : {}),
              }}
            >
              + New Trade
            </button>
          </div>
          {atLimit && (
            <div
              role="alert"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                margin: '0.5rem 0 1rem',
                padding: '0.75rem 1rem',
                border: '1px solid var(--color-warning)',
                borderRadius: '6px',
                backgroundColor: 'var(--color-chip-warning-bg)',
                color: 'var(--color-chip-warning-text)',
                fontSize: '0.9rem',
              }}
            >
              <svg
                width={20}
                height={20}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                style={{ flexShrink: 0, marginTop: '0.1rem' }}
              >
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span className="trial-limit-message">{limitReason}</span>
                <button
                  type="button"
                  className="upgrade-cta"
                  onClick={handleViewPlans}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem 1rem',
                    minHeight: '44px',
                    fontSize: '0.9rem',
                  }}
                >
                  View plans
                </button>
              </div>
            </div>
          )}
          {showCheckoutSuccess && (
            <div
              role="status"
              style={{
                margin: '0.5rem 0 1rem',
                padding: '0.75rem 1rem',
                border: '1px solid var(--color-success)',
                borderRadius: '6px',
                backgroundColor: 'var(--color-chip-success-bg)',
                color: 'var(--color-chip-success-text)',
                fontSize: '0.9rem',
              }}
            >
              {planUsage ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                  <PlanTierIcon planId={planUsage.planId} size={22} />
                  {`Payment received. Your plan is now ${planDisplayName(planUsage.planId)}.`}
                </span>
              ) : (
                'Payment received. Your plan is being updated.'
              )}
              <button
                type="button"
                onClick={() => setShowCheckoutSuccess(false)}
                style={{
                  marginLeft: '0.75rem',
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-brand)',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  textDecoration: 'underline',
                  padding: '0.25rem',
                  minHeight: '44px',
                }}
              >
                Dismiss
              </button>
            </div>
          )}
          <TradeList />
        </div>
      )}
    </div>
  );
}
